import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit, Trash2, Eye, Copy, Loader2, FileText, LayoutTemplate } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const CATEGORIES = [
  { value: 'contact', label: 'Kontakt' },
  { value: 'survey', label: 'Umfrage' },
  { value: 'registration', label: 'Anmeldung' },
  { value: 'feedback', label: 'Feedback' },
  { value: 'application', label: 'Bewerbung' },
  { value: 'order', label: 'Bestellung' },
  { value: 'other', label: 'Sonstige' },
];

const Templates = () => {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'other',
    fields: [],
    theme: { primaryColor: '#3b82f6', backgroundColor: '#ffffff' }
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/form-templates`, { headers: getAuthHeader() });
      setTemplates(res.data || []);
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Name ist erforderlich');
      return;
    }

    try {
      if (selectedTemplate) {
        await axios.put(`${API_URL}/api/form-templates/${selectedTemplate.id}`, formData, { headers: getAuthHeader() });
        toast.success(t('success.updated'));
      } else {
        await axios.post(`${API_URL}/api/form-templates`, formData, { headers: getAuthHeader() });
        toast.success(t('success.created'));
      }
      setDialogOpen(false);
      fetchTemplates();
      resetForm();
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleDelete = async () => {
    if (!selectedTemplate) return;
    try {
      await axios.delete(`${API_URL}/api/form-templates/${selectedTemplate.id}`, { headers: getAuthHeader() });
      toast.success(t('success.deleted'));
      setDeleteDialogOpen(false);
      setSelectedTemplate(null);
      fetchTemplates();
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const openEdit = (template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name || '',
      description: template.description || '',
      category: template.category || 'other',
      fields: template.fields || [],
      theme: template.theme || { primaryColor: '#3b82f6', backgroundColor: '#ffffff' }
    });
    setDialogOpen(true);
  };

  const openCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const resetForm = () => {
    setSelectedTemplate(null);
    setFormData({
      name: '',
      description: '',
      category: 'other',
      fields: [],
      theme: { primaryColor: '#3b82f6', backgroundColor: '#ffffff' }
    });
  };

  const openPreview = (template) => {
    setSelectedTemplate(template);
    setPreviewOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="templates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">{t('templates.title')}</h1>
          <p className="text-slate-600 mt-1">Wiederverwendbare Formularvorlagen verwalten</p>
        </div>
        <Button onClick={openCreate} data-testid="new-template-btn">
          <Plus className="h-4 w-4 mr-2" />
          {t('templates.newTemplate')}
        </Button>
      </div>

      {/* Templates Grid */}
      {templates.length === 0 ? (
        <Card className="border-slate-200/60">
          <CardContent className="py-12 text-center">
            <LayoutTemplate className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-700">{t('templates.noTemplates')}</h3>
            <p className="text-slate-500 mt-1">{t('templates.createFirst')}</p>
            <Button onClick={openCreate} className="mt-4">
              <Plus className="h-4 w-4 mr-2" />
              {t('templates.newTemplate')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => (
            <Card key={template.id} className="border-slate-200/60 hover:shadow-md transition-shadow" data-testid={`template-${template.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                      <FileText className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{template.name}</CardTitle>
                      <Badge variant="outline" className="mt-1 text-xs">
                        {CATEGORIES.find(c => c.value === template.category)?.label || template.category}
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 line-clamp-2 mb-4">
                  {template.description || 'Keine Beschreibung'}
                </p>
                <div className="text-xs text-slate-500 mb-4">
                  {template.fields?.length || 0} Felder definiert
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => openPreview(template)}>
                    <Eye className="h-3 w-3 mr-1" />
                    {t('common.preview')}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => openEdit(template)}>
                    <Edit className="h-3 w-3" />
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => { setSelectedTemplate(template); setDeleteDialogOpen(true); }}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedTemplate ? t('templates.editTemplate') : t('templates.newTemplate')}</DialogTitle>
            <DialogDescription>Erstellen Sie wiederverwendbare Formularvorlagen</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>{t('templates.templateName')} *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="z.B. Kontaktformular Standard"
              />
            </div>
            
            <div>
              <Label>{t('templates.templateDescription')}</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Beschreibung der Vorlage..."
                rows={3}
              />
            </div>
            
            <div>
              <Label>{t('templates.category')}</Label>
              <Select value={formData.category} onValueChange={(val) => setFormData({ ...formData, category: val })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Primärfarbe</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="color"
                    value={formData.theme?.primaryColor || '#3b82f6'}
                    onChange={(e) => setFormData({ ...formData, theme: { ...formData.theme, primaryColor: e.target.value } })}
                    className="w-12 h-10 p-1"
                  />
                  <Input
                    value={formData.theme?.primaryColor || '#3b82f6'}
                    onChange={(e) => setFormData({ ...formData, theme: { ...formData.theme, primaryColor: e.target.value } })}
                  />
                </div>
              </div>
              <div>
                <Label>Hintergrundfarbe</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="color"
                    value={formData.theme?.backgroundColor || '#ffffff'}
                    onChange={(e) => setFormData({ ...formData, theme: { ...formData.theme, backgroundColor: e.target.value } })}
                    className="w-12 h-10 p-1"
                  />
                  <Input
                    value={formData.theme?.backgroundColor || '#ffffff'}
                    onChange={(e) => setFormData({ ...formData, theme: { ...formData.theme, backgroundColor: e.target.value } })}
                  />
                </div>
              </div>
            </div>

            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm text-slate-600">
                <strong>Hinweis:</strong> Felder können im Formular-Editor hinzugefügt werden, 
                nachdem Sie ein Formular aus dieser Vorlage erstellt haben.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSave}>{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedTemplate?.name} - {t('common.preview')}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="border rounded-lg p-4" style={{ backgroundColor: selectedTemplate?.theme?.backgroundColor }}>
              <h3 className="text-lg font-semibold mb-2" style={{ color: selectedTemplate?.theme?.primaryColor }}>
                {selectedTemplate?.name}
              </h3>
              <p className="text-sm text-slate-600 mb-4">{selectedTemplate?.description}</p>
              <div className="space-y-3">
                {(selectedTemplate?.fields || []).length === 0 ? (
                  <p className="text-sm text-slate-500 italic">Keine Felder definiert</p>
                ) : (
                  (selectedTemplate?.fields || []).map((field, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <span className="text-slate-400">{idx + 1}.</span>
                      <span>{field.label || field.field_id}</span>
                      <Badge variant="outline" className="text-xs">{field.field_type}</Badge>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>{t('common.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Vorlage löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie die Vorlage "{selectedTemplate?.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Templates;
